import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import apiService from '../../services/api'
import { HiOutlineBuildingOffice, HiOutlineChevronRight } from 'react-icons/hi2'
import { Spinner } from '../ui'

const WorkspaceDropdown = ({ onClose }) => {
  const { t } = useTranslation()
  const { switchWorkspace } = useAuth()
  const { getEntity } = useWorkspace()

  // Dynamic terminology
  const ownerTerm = getEntity('owner')
  const workspaceTerm = getEntity('workspace')
  const connectionTerm = getEntity('connection')
  const navigate = useNavigate()
  const [connections, setConnections] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchConnections()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchConnections = async () => {
    try {
      setLoading(true)
      const response = await apiService.get('/my-following/')

      if (response.success) {
        const switchableConnections = (response.data.data || []).filter(
          conn => conn.can_switch_to === true,
        )
        setConnections(switchableConnections)
      } else {
        setError(response.error?.message || t('workspace.loadFailed', { entity: connectionTerm.plural.toLowerCase() }))
      }
    } catch {
      setError(t('workspace.loadFailed', { entity: connectionTerm.plural.toLowerCase() }))
    } finally {
      setLoading(false)
    }
  }

  const handleSwitchWorkspace = (connection) => {
    switchWorkspace(connection.id, connection.name)
    navigate(`/dashboard?owner_account_id=${connection.id}`)
    onClose()
  }

  return (
    <div className="absolute end-0 mt-2 w-80 bg-elevated rounded-xl shadow-token-lg border border-border py-2 z-50">
      {/* Header */}
      <div className="px-4 py-2 border-b border-border-subtle">
        <h3 className="font-semibold text-ink-primary">{t('workspace.switchWorkspace', { entity: workspaceTerm.singular })}</h3>
        <p className="text-xs text-ink-muted mt-1">
          {t('workspace.accessOwnerAccounts', { entity: ownerTerm.singular.toLowerCase() })}
        </p>
      </div>

      {/* Content */}
      <div className="max-h-64 overflow-y-auto">
        {loading && (
          <div className="px-4 py-6 text-center">
            <div className="flex justify-center mb-2"><Spinner size="sm" /></div>
            <p className="text-sm text-ink-muted">{t('workspace.loadingConnections', { entity: connectionTerm.plural.toLowerCase() })}</p>
          </div>
        )}

        {error && (
          <div className="px-4 py-4">
            <p className="text-sm text-danger">{error}</p>
          </div>
        )}

        {!loading && !error && connections.length === 0 && (
          <div className="px-4 py-6 text-center">
            <HiOutlineBuildingOffice className="w-8 h-8 text-ink-disabled mx-auto mb-2" />
            <p className="text-sm text-ink-muted mb-1">{t('workspace.noneAvailable', { entity: connectionTerm.plural.toLowerCase() })}</p>
            <p className="text-xs text-ink-muted">
              {t('workspace.connectToAccess', {
                owner: ownerTerm.plural.toLowerCase(),
                workspace: workspaceTerm.plural.toLowerCase(),
              })}
            </p>
          </div>
        )}

        {!loading && !error && connections.length > 0 && (
          <div className="py-1">
            {connections.map((connection) => (
              <button
                key={connection.id}
                onClick={() => handleSwitchWorkspace(connection)}
                className="w-full px-4 py-3 text-start hover:bg-muted transition-colors flex items-center justify-between group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-brand-soft rounded-lg flex items-center justify-center">
                    <span className="text-brand font-semibold text-sm">
                      {connection.name?.charAt(0)?.toUpperCase() || 'L'}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-ink-primary truncate">
                      {connection.name}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-success font-medium">
                        {t('workspace.canSwitch')}
                      </span>
                      <span className="text-xs text-ink-muted">
                        • {ownerTerm.singular}
                      </span>
                    </div>
                  </div>
                </div>
                <HiOutlineChevronRight className="w-4 h-4 text-ink-muted group-hover:text-ink-secondary rtl:rotate-180" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {!loading && !error && connections.length > 0 && (
        <div className="px-4 py-2 border-t border-border-subtle">
          <p className="text-xs text-ink-muted">
            {t('workspace.clickToSwitch', { entity: workspaceTerm.singular.toLowerCase() })}
          </p>
        </div>
      )}
    </div>
  )
}

export default WorkspaceDropdown
